// Offline-Puffer fuer persoenliche Stempel (/me/checkins, TQ #556). Ein Tipp auf "Einstempeln"
// schreibt SOFORT in IndexedDB (eventId + Zeit beim Tippen) und ist damit fuer den Mitarbeiter
// erledigt; gesendet wird danach im Hintergrund des Hauptthreads -- der Service Worker hat
// kein Token und kann das nicht (siehe flush-Trigger in MeinPage). Die API ist ueber event_id
// idempotent, eine Wiederholung erzeugt also keinen zweiten Stempel.

import type { LogType } from './canonicalMessage'
import {
  deletePersonalEntries,
  enqueuePersonalEntry,
  getPersonalEntries,
  getPersonalPending,
  updatePersonalEntry,
  type PersonalOutboxEntry,
} from './db'
import { submitPersonalCheckin, type CheckinStatusResponse, type PersonalCheckinOptions } from './api'
import { AuthTemporarilyUnavailableError, SessionExpiredError, callWithFreshToken } from './auth'
import { backoffDelayMs } from './sync'

/** Wird auf `window` gefeuert, sobald sich die Outbox aendert (UI laedt dann neu). */
export const PERSONAL_OUTBOX_EVENT = 'personal-outbox-changed'

const DAY_MS = 24 * 60 * 60_000
const KEEP_SENT_MS = 2 * DAY_MS
const KEEP_KONFLIKT_MS = 7 * DAY_MS
const KEEP_REJECTED_MS = 30 * DAY_MS

function notifyChanged() {
  window.dispatchEvent(new Event(PERSONAL_OUTBOX_EVENT))
}

export interface RecordResult {
  eventId: string
  /** false: IndexedDB nicht nutzbar, der Stempel wurde direkt gesendet (alter Weg). */
  queued: boolean
}

/**
 * Schreibt den Stempel lokal in die Outbox und stoesst das Senden an (nicht abgewartet).
 * Ist IndexedDB nicht verfuegbar, faellt die Funktion auf direktes Senden zurueck; dann
 * werfen Netz-/Auth-Fehler wie frueher an den Aufrufer.
 */
export async function recordPersonalCheckin(
  employeeIdentifier: string,
  logType: LogType,
  opts: Pick<PersonalCheckinOptions, 'auftragId' | 'taetigkeitGeplant' | 'taetigkeitIst' | 'zusatzgeraet'> = {},
): Promise<RecordResult> {
  const eventId = crypto.randomUUID()
  const clientTimeIso = new Date().toISOString()
  try {
    await enqueuePersonalEntry({
      eventId,
      employeeIdentifier,
      logType,
      clientTimeIso,
      createdAt: clientTimeIso,
      auftragId: opts.auftragId,
      taetigkeitGeplant: opts.taetigkeitGeplant,
      taetigkeitIst: opts.taetigkeitIst,
      zusatzgeraet: opts.zusatzgeraet,
    })
  } catch {
    await callWithFreshToken((t) =>
      submitPersonalCheckin(t, logType, { ...opts, eventId, clientTimeIso }),
    )
    return { eventId, queued: false }
  }
  notifyChanged()
  void flushPersonalOutbox(employeeIdentifier)
  return { eventId, queued: true }
}

function isDue(entry: PersonalOutboxEntry, now: number): boolean {
  if (entry.attempts === 0 || !entry.lastAttemptAt) return true
  return now - new Date(entry.lastAttemptAt).getTime() >= backoffDelayMs(entry.attempts)
}

type ErrorKind = 'session' | 'transient' | 'limited' | 'permanent'

/** 403/404 koennen voruebergehend sein (Proxy-404 waehrend eines Rollouts, kurzes 403 durch
 * Rechte-/Sessionwechsel). Sie werden so oft wiederholt (Backoff wie sonst, ca. 10 Minuten),
 * danach gilt der Eintrag als abgelehnt. Ein Stempel geht so nicht wegen eines kurzen
 * Ausfalls verloren, ein dauerhaft falscher Stempel blockiert die Queue aber nicht ewig. */
export const MAX_LIMITED_ATTEMPTS = 8

function classify(e: unknown): ErrorKind {
  if (e instanceof SessionExpiredError) return 'session'
  if (e instanceof AuthTemporarilyUnavailableError) return 'transient'
  const status = (e as { status?: number } | null)?.status
  // Kein Status = Netzwerkfehler (fetch wirft TypeError).
  if (typeof status !== 'number') return 'transient'
  if (status >= 500 || status === 401 || status === 408 || status === 429) return 'transient'
  if (status === 403 || status === 404) return 'limited'
  return 'permanent' // uebrige 4xx (inkl. 409 fremde event_id): Wiederholen aendert nichts
}

export interface PersonalFlushResult {
  sent: number
  konflikt: number
  rejected: number
  remaining: number
  /** Anmeldung endgueltig ungueltig: Eintraege bleiben erhalten, Re-Login noetig. */
  sessionExpired: boolean
}

let inFlight: Promise<PersonalFlushResult> | null = null

/**
 * Sendet faellige Eintraege strikt sequenziell nach Zeit beim Tippen. Ein noch nicht
 * faelliger oder transient fehlgeschlagener Eintrag haelt die spaeteren zurueck (Reihenfolge),
 * ein abgelehnter (4xx) nicht. Mit `employeeIdentifier` werden nur Eintraege dieses
 * Mitarbeiters gesendet (geteiltes Geraet: nie mit fremdem Token).
 * Parallele Aufrufe (Tab, Trigger) teilen sich einen Lauf; zwischen Tabs sperrt
 * navigator.locks, wo vorhanden -- sonst faengt die API-Idempotenz ein Doppelsenden ab.
 */
export function flushPersonalOutbox(employeeIdentifier?: string): Promise<PersonalFlushResult> {
  if (!inFlight) {
    inFlight = runExclusive(() => doFlush(employeeIdentifier)).finally(() => {
      inFlight = null
    })
  }
  return inFlight
}

async function runExclusive(fn: () => Promise<PersonalFlushResult>): Promise<PersonalFlushResult> {
  const locks = (navigator as Navigator & { locks?: LockManager }).locks
  if (!locks) return fn()
  const result = await locks.request('stempel-personal-outbox', { ifAvailable: true }, (lock) =>
    lock ? fn() : null,
  )
  // Anderer Tab sendet gerade: nichts doppelt tun, Stand nur lesen.
  return result ?? { sent: 0, konflikt: 0, rejected: 0, remaining: (await getPersonalPending()).length, sessionExpired: false }
}

async function doFlush(employeeIdentifier?: string): Promise<PersonalFlushResult> {
  const result: PersonalFlushResult = { sent: 0, konflikt: 0, rejected: 0, remaining: 0, sessionExpired: false }
  const pending = (await getPersonalPending())
    .filter((e) => !employeeIdentifier || e.employeeIdentifier === employeeIdentifier)
    .sort((a, b) => a.clientTimeIso.localeCompare(b.clientTimeIso))
  const now = Date.now()

  for (const entry of pending) {
    if (!isDue(entry, now)) break
    try {
      const response = await callWithFreshToken((t) =>
        submitPersonalCheckin(t, entry.logType, {
          eventId: entry.eventId,
          clientTimeIso: entry.clientTimeIso,
          auftragId: entry.auftragId,
          taetigkeitGeplant: entry.taetigkeitGeplant,
          taetigkeitIst: entry.taetigkeitIst,
          zusatzgeraet: entry.zusatzgeraet,
        }),
      )
      // 2xx (accepted oder already_known) ist Erfolg. Spiegelt die Antwort unsere eventId nicht
      // zurueck, kennt der Server das Feld nicht (aelterer Stand): angenommen, aber ohne
      // Idempotenz-Absicherung -- wird vermerkt.
      const konflikt = response.konflikt === true
      await updatePersonalEntry(entry.eventId, {
        status: konflikt ? 'konflikt' : 'sent',
        konfliktGrund: konflikt ? response.konfliktGrund || null : null,
        serverIgnoredEventId: response.eventId !== entry.eventId,
        attempts: entry.attempts + 1,
        lastAttemptAt: new Date().toISOString(),
        lastError: null,
      })
      if (konflikt) result.konflikt++
      else result.sent++
    } catch (e) {
      const classified = classify(e)
      // 'limited': begrenzt wiederholen -- erst der letzte erlaubte Versuch wird zu permanent.
      const kind: Exclude<ErrorKind, 'limited'> =
        classified === 'limited'
          ? entry.attempts + 1 >= MAX_LIMITED_ATTEMPTS
            ? 'permanent'
            : 'transient'
          : classified
      if (kind === 'session') {
        result.sessionExpired = true
        break // Eintraege unveraendert behalten
      }
      const message = e instanceof Error ? e.message : String(e)
      await updatePersonalEntry(entry.eventId, {
        status: kind === 'permanent' ? 'rejected' : 'pending',
        attempts: entry.attempts + 1,
        lastAttemptAt: new Date().toISOString(),
        lastError: message,
      })
      if (kind === 'permanent') {
        result.rejected++
        continue
      }
      break
    }
  }

  await prune()
  result.remaining = (await getPersonalPending()).length
  notifyChanged()
  return result
}

async function prune() {
  const now = Date.now()
  const old = (await getPersonalEntries())
    .filter((e) => {
      const age = now - new Date(e.createdAt).getTime()
      return (
        (e.status === 'sent' && age > KEEP_SENT_MS) ||
        (e.status === 'konflikt' && age > KEEP_KONFLIKT_MS) ||
        (e.status === 'rejected' && age > KEEP_REJECTED_MS)
      )
    })
    .map((e) => e.eventId)
  if (old.length > 0) await deletePersonalEntries(old)
}

/**
 * Legt lokal noch nicht (sichtbar) verarbeitete Kommen/Gehen-Stempel ueber den
 * Server-Status, damit offline nach dem Einstempeln nicht "Ausgestempelt" steht. Ein Eintrag
 * gilt, wenn er nicht abgelehnt ist und juenger als der letzte Server-Stempel. ANFAHRT/ABFAHRT
 * aendern den Anwesenheitsstatus nicht.
 */
export function applyLocalEntries(
  server: CheckinStatusResponse | null,
  entries: PersonalOutboxEntry[],
  employeeIdentifier: string,
): CheckinStatusResponse | null {
  const latest = entries
    .filter(
      (e) =>
        e.employeeIdentifier === employeeIdentifier &&
        e.status !== 'rejected' &&
        (e.logType === 'IN' || e.logType === 'OUT'),
    )
    .sort((a, b) => b.clientTimeIso.localeCompare(a.clientTimeIso))[0]
  if (!latest) return server
  if (server?.last_time && new Date(server.last_time).getTime() >= new Date(latest.clientTimeIso).getTime()) {
    return server
  }
  return {
    employee_identifier: employeeIdentifier,
    last_log_type: latest.logType,
    last_time: latest.clientTimeIso,
    is_checked_in: latest.logType === 'IN',
  }
}

const LOG_LABEL: Record<LogType, string> = {
  IN: 'Einstempeln',
  OUT: 'Ausstempeln',
  ANFAHRT: 'Anfahrt',
  ABFAHRT: 'Abfahrt',
}

const SENT_VISIBLE_MS = 10 * 60_000

export interface EntryLine {
  eventId: string
  text: string
  tone: 'info' | 'ok' | 'error'
}

/** Statuszeilen fuer die Anzeige: ausstehende, abgelehnte und zu pruefende Eintraege (24 h),
 * erfolgreich gesendete nur kurz. Konflikt ist KEIN Alarm -- der Stempel ist angekommen,
 * das Buero klaert ihn in ERPNext. */
export function entryLines(entries: PersonalOutboxEntry[], now = Date.now()): EntryLine[] {
  const lines: EntryLine[] = []
  for (const e of [...entries].sort((a, b) => b.clientTimeIso.localeCompare(a.clientTimeIso))) {
    const age = now - new Date(e.createdAt).getTime()
    const time = new Date(e.clientTimeIso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    const head = `${LOG_LABEL[e.logType]} ${time}`
    if (e.status === 'pending') {
      lines.push({ eventId: e.eventId, text: `${head}: Gespeichert, wird gesendet`, tone: 'info' })
    } else if (e.status === 'sent' && age < SENT_VISIBLE_MS) {
      lines.push({ eventId: e.eventId, text: `${head}: Gesendet ✓`, tone: 'ok' })
    } else if (e.status === 'konflikt' && age < DAY_MS) {
      lines.push({ eventId: e.eventId, text: `${head}: Gesendet, wird geprüft`, tone: 'info' })
    } else if (e.status === 'rejected') {
      lines.push({
        eventId: e.eventId,
        text: `${head}: Nicht angenommen (${e.lastError ?? 'unbekannter Fehler'}). Bitte im Büro melden.`,
        tone: 'error',
      })
    }
  }
  return lines
}
